/**
 * Canonical country name variants for import + country sync + filter match.
 * Store full INVESTOR_COUNTRIES names only (New Zealand, never NZ).
 * Keys are lowercase whole-cell matches. No city/state/region extraction.
 * No "Australia & NZ" / Global. Melbourne is import-only (see IMPORT_COUNTRY_ALIASES).
 */

const COUNTRY_MAPPINGS = Object.freeze({
  // ISO-2
  ar: 'Argentina',
  au: 'Australia',
  at: 'Austria',
  bh: 'Bahrain',
  be: 'Belgium',
  br: 'Brazil',
  bg: 'Bulgaria',
  ca: 'Canada',
  cl: 'Chile',
  cn: 'China',
  co: 'Colombia',
  hr: 'Croatia',
  cz: 'Czech Republic',
  dk: 'Denmark',
  eg: 'Egypt',
  ee: 'Estonia',
  fi: 'Finland',
  fr: 'France',
  de: 'Germany',
  gr: 'Greece',
  hk: 'Hong Kong',
  hu: 'Hungary',
  is: 'Iceland',
  in: 'India',
  id: 'Indonesia',
  ie: 'Ireland',
  il: 'Israel',
  it: 'Italy',
  jp: 'Japan',
  ke: 'Kenya',
  kw: 'Kuwait',
  lv: 'Latvia',
  li: 'Liechtenstein',
  lt: 'Lithuania',
  lu: 'Luxembourg',
  my: 'Malaysia',
  mx: 'Mexico',
  mc: 'Monaco',
  nl: 'Netherlands',
  nz: 'New Zealand',
  ng: 'Nigeria',
  no: 'Norway',
  ph: 'Philippines',
  pl: 'Poland',
  pt: 'Portugal',
  qa: 'Qatar',
  ro: 'Romania',
  ru: 'Russia',
  sa: 'Saudi Arabia',
  rs: 'Serbia',
  sg: 'Singapore',
  si: 'Slovenia',
  za: 'South Africa',
  kr: 'South Korea',
  es: 'Spain',
  se: 'Sweden',
  ch: 'Switzerland',
  tw: 'Taiwan',
  th: 'Thailand',
  tr: 'Turkey',
  ua: 'Ukraine',
  ae: 'United Arab Emirates',
  gb: 'United Kingdom',
  uk: 'United Kingdom',
  us: 'United States',
  vn: 'Vietnam',

  // ISO-3 / common codes
  arg: 'Argentina',
  aus: 'Australia',
  aut: 'Austria',
  bhr: 'Bahrain',
  bel: 'Belgium',
  bra: 'Brazil',
  bgr: 'Bulgaria',
  can: 'Canada',
  chl: 'Chile',
  chn: 'China',
  col: 'Colombia',
  hrv: 'Croatia',
  cze: 'Czech Republic',
  dnk: 'Denmark',
  egy: 'Egypt',
  est: 'Estonia',
  fin: 'Finland',
  fra: 'France',
  deu: 'Germany',
  grc: 'Greece',
  hkg: 'Hong Kong',
  hun: 'Hungary',
  isl: 'Iceland',
  ind: 'India',
  idn: 'Indonesia',
  irl: 'Ireland',
  isr: 'Israel',
  ita: 'Italy',
  jpn: 'Japan',
  ken: 'Kenya',
  kwt: 'Kuwait',
  lva: 'Latvia',
  lie: 'Liechtenstein',
  ltu: 'Lithuania',
  lux: 'Luxembourg',
  mys: 'Malaysia',
  mex: 'Mexico',
  mco: 'Monaco',
  nld: 'Netherlands',
  nzl: 'New Zealand',
  nga: 'Nigeria',
  nor: 'Norway',
  phl: 'Philippines',
  pol: 'Poland',
  prt: 'Portugal',
  qat: 'Qatar',
  rou: 'Romania',
  rus: 'Russia',
  sau: 'Saudi Arabia',
  srb: 'Serbia',
  sgp: 'Singapore',
  svn: 'Slovenia',
  zaf: 'South Africa',
  kor: 'South Korea',
  esp: 'Spain',
  swe: 'Sweden',
  che: 'Switzerland',
  twn: 'Taiwan',
  tha: 'Thailand',
  tur: 'Turkey',
  ukr: 'Ukraine',
  are: 'United Arab Emirates',
  gbr: 'United Kingdom',
  usa: 'United States',
  vnm: 'Vietnam',

  // Name variants (not cities)
  argentina: 'Argentina',
  australia: 'Australia',
  austria: 'Austria',
  bahrain: 'Bahrain',
  belgium: 'Belgium',
  brazil: 'Brazil',
  bulgaria: 'Bulgaria',
  canada: 'Canada',
  chile: 'Chile',
  china: 'China',
  colombia: 'Colombia',
  croatia: 'Croatia',
  'czech republic': 'Czech Republic',
  czechia: 'Czech Republic',
  denmark: 'Denmark',
  egypt: 'Egypt',
  estonia: 'Estonia',
  finland: 'Finland',
  france: 'France',
  germany: 'Germany',
  deutschland: 'Germany',
  greece: 'Greece',
  'hong kong': 'Hong Kong',
  hungary: 'Hungary',
  iceland: 'Iceland',
  india: 'India',
  indonesia: 'Indonesia',
  ireland: 'Ireland',
  israel: 'Israel',
  italy: 'Italy',
  japan: 'Japan',
  kenya: 'Kenya',
  kuwait: 'Kuwait',
  latvia: 'Latvia',
  liechtenstein: 'Liechtenstein',
  lithuania: 'Lithuania',
  luxembourg: 'Luxembourg',
  malaysia: 'Malaysia',
  mexico: 'Mexico',
  monaco: 'Monaco',
  netherlands: 'Netherlands',
  holland: 'Netherlands',
  'the netherlands': 'Netherlands',
  'new zealand': 'New Zealand',
  nigeria: 'Nigeria',
  norway: 'Norway',
  philippines: 'Philippines',
  poland: 'Poland',
  portugal: 'Portugal',
  qatar: 'Qatar',
  romania: 'Romania',
  russia: 'Russia',
  'saudi arabia': 'Saudi Arabia',
  ksa: 'Saudi Arabia',
  serbia: 'Serbia',
  singapore: 'Singapore',
  slovenia: 'Slovenia',
  'south africa': 'South Africa',
  'south korea': 'South Korea',
  korea: 'South Korea',
  'republic of korea': 'South Korea',
  spain: 'Spain',
  sweden: 'Sweden',
  switzerland: 'Switzerland',
  taiwan: 'Taiwan',
  thailand: 'Thailand',
  turkey: 'Turkey',
  ukraine: 'Ukraine',
  'united arab emirates': 'United Arab Emirates',
  uae: 'United Arab Emirates',
  'united kingdom': 'United Kingdom',
  'great britain': 'United Kingdom',
  britain: 'United Kingdom',
  england: 'United Kingdom',
  scotland: 'United Kingdom',
  wales: 'United Kingdom',
  'northern ireland': 'United Kingdom',
  'united states': 'United States',
  'united states of america': 'United States',
  'u.s.': 'United States',
  'u.s.a.': 'United States',
  'u.s.a': 'United States',
  vietnam: 'Vietnam',
  'viet nam': 'Vietnam',
});

const IMPORT_COUNTRY_ALIASES = Object.freeze({
  ...COUNTRY_MAPPINGS,
  melbourne: 'Australia',
});

module.exports = {
  COUNTRY_MAPPINGS,
  IMPORT_COUNTRY_ALIASES,
};
